import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Users, Award, TrendingUp, CheckCircle2, Shield } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Target,
      title: "Mission",
      description: "Aider les hôtes Airbnb à maintenir une note 4.9★+ grâce à un service de nettoyage fiable et professionnel."
    },
    {
      icon: Award,
      title: "Excellence",
      description: "Nous appliquons une checklist de 27 points à chaque intervention. Aucun détail n'est laissé au hasard."
    },
    {
      icon: Shield,
      title: "Garantie",
      description: "Si un guest se plaint de la propreté dans les 24h, on re-nettoie gratuitement ou vous êtes remboursé 100%."
    },
    {
      icon: Users,
      title: "Équipe",
      description: "Tous nos agents sont formés aux standards Airbnb et équipés de produits professionnels."
    }
  ];

  const timeline = [
    {
      year: "2025",
      title: "Lancement à Meknès",
      description: "Début de NeoNkawa avec une vision claire : devenir le service #1 pour les hôtes Airbnb à Meknès."
    },
    {
      year: "6 mois",
      title: "10 Clients Réguliers",
      description: "Rapidement adopté par les premiers Superhosts grâce au bouche-à-oreille et aux résultats."
    },
    {
      year: "12 mois",
      title: "50+ Hôtes Satisfaits",
      description: "Expansion de l'équipe et mise en place du système d'abonnement mensuel."
    },
    {
      year: "Aujourd'hui",
      title: "Leader à Meknès",
      description: "200+ nettoyages complétés, 98% de satisfaction, note moyenne 4.9★."
    }
  ];

  const whyDifferent = [
    {
      title: "Photos Systématiques",
      description: "On envoie des photos avant/pendant/après sur WhatsApp. Vous savez exactement ce qui a été fait."
    },
    {
      title: "Checklist Standardisée",
      description: "27 points vérifiés à chaque fois. Même qualité, même agent ou pas."
    },
    {
      title: "Réponse Ultra-Rapide",
      description: "Moins de 15 minutes pour répondre à votre message. Toujours."
    },
    {
      title: "Pricing Transparent",
      description: "Pas de frais cachés. Le prix qu'on vous dit = le prix que vous payez."
    },
    {
      title: "Produits Inclus",
      description: "Vous n'avez rien à fournir. On arrive avec tout le matériel professionnel."
    },
    {
      title: "Garantie Satisfaction",
      description: "Si vous n'êtes pas satisfait dans les 24h, on re-fait ou on rembourse. Point."
    }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <h1>À Propos de NeoNkawa</h1>
          <p>Le service de nettoyage Airbnb qui protège votre note 4.9★</p>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section">
        <div className="section-container">
          <div className="story-content">
            <h2>Notre Histoire</h2>
            <p className="story-intro">
              NeoNkawa est né d'un constat simple : les hôtes Airbnb à Meknès méritent un service de nettoyage fiable, professionnel et transparent.
            </p>
            <p>
              Trop d'hôtes perdaient leur statut Superhost à cause de problèmes de propreté. Trop de stress avant chaque check-in. Trop de nettoyeurs non fiables qui viennent "quand ils peuvent".
            </p>
            <p>
              Nous avons créé NeoNkawa pour résoudre ce problème. Un service où la ponctualité n'est pas une option, où la qualité est garantie par une checklist standardisée, et où vous recevez des photos après chaque intervention.
            </p>
            <p className="story-highlight">
              Résultat ? 50+ hôtes satisfaits, 200+ nettoyages complétés, et une note moyenne de 4.9★ sur Airbnb pour nos clients.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="section-container">
          <h2>Nos Valeurs</h2>
          <div className="values-grid">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="value-card">
                  <div className="value-icon">
                    <IconComponent size={40} />
                  </div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="timeline-section">
        <div className="section-container">
          <h2>Notre Parcours</h2>
          <div className="timeline">
            {timeline.map((item, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-marker">
                  <TrendingUp size={24} />
                </div>
                <div className="timeline-content">
                  <div className="timeline-year">{item.year}</div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Different Section */}
      <section className="why-different-section">
        <div className="section-container">
          <h2>Pourquoi Sommes-Nous Différents?</h2>
          <div className="why-different-grid">
            {whyDifferent.map((item, index) => (
              <div key={index} className="why-different-item">
                <CheckCircle2 size={24} color="#00C878" />
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-cta">
        <h2>Travaillons Ensemble</h2>
        <p>Rejoignez les Superhosts qui nous font confiance</p>
        <div className="cta-buttons">
          <Link to="/contact" className="white-btn">Commencer Maintenant</Link>
          <Link to="/services" className="outline-white-btn">Découvrir Nos Services</Link>
        </div>
      </section>
    </div>
  );
};

export default About;
